import {inject, Injectable} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
  DialogWidth,
  MapRoles,
  SessionService,
  SnackBarService,
  UserMACR,
  Map
} from '@q9elements/ui-kit/common';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {EMPTY, map, Observable, OperatorFunction} from 'rxjs';
import {
  MapShareDaoService,
  MapShareDialogComponent,
  Q9MapsDaoService,
  SearchCandidate,
  SearchCandidateResponse
} from '@q9elements/ui-kit/maps';
import {PopupManagerService} from '../services/popup-manager.service';
import {shareDiagramLink} from '../../shared/constants/elements-links';

export type MapSuggestInvite = {
  sharedMap: Map;
  presetUser: SearchCandidate | string;
};

@Injectable({
  providedIn: 'root'
})
export class MapRequestAccessService {
  private readonly snackBarService = inject(SnackBarService);
  private readonly daoService = inject(Q9MapsDaoService);
  private readonly sessionService = inject(SessionService);
  private readonly mapShareDaoService = inject(MapShareDaoService);

  constructor(private popupSvc: PopupManagerService) {}

  public handlePresetUserInvite(route: ActivatedRoute): void {
    const {id, userEmail} = route.snapshot.queryParams;

    if (!userEmail) {
      return;
    }

    this.handleAccessRightsRequest(id, userEmail);
  }

  handleAccessRightsRequest(id: string, userId: string): void {
    this.getCandidateForInvite(id, userId).subscribe((response: MapSuggestInvite) =>
      this.openInviteDialog(response)
    );
  }

  getCandidateForInvite(id: string, userId: string): Observable<MapSuggestInvite> {
    return this.daoService.getMap(id).pipe(
      switchMap((sharedMap: Map) => {
        return this.mapShareDaoService.findCandidates(id, userId).pipe(
          map(({usersToAdd}: SearchCandidateResponse) => {
            const [presetUser] = usersToAdd?.length ? usersToAdd : [userId];

            return {sharedMap, presetUser};
          })
        );
      }),
      this.handleError()
    );
  }

  openInviteDialog({sharedMap, presetUser}: MapSuggestInvite): void {
    const macr = {
      canCopy: true,
      canGrantAccess: true,
      canAddNotes: true,
      role: MapRoles.MANAGE
    };

    this.openInviteUserDialog(sharedMap, macr, presetUser);
  }

  public openInviteUserDialog(
    map: Map,
    user: Pick<UserMACR, 'canCopy' | 'canGrantAccess' | 'role'>,
    presetUser?: SearchCandidate | string
  ) {
    return this.popupSvc
      .open(MapShareDialogComponent, {
        width: DialogWidth.LG,
        data: {
          link: shareDiagramLink,
          modalTitle: 'SHARE_DIAGRAM.INVITE_MODAL_TITLE',
          confirmText: 'SHARE_DIAGRAM.INVITE_MODAL_BTN_INVITE',
          sharedMap: map,
          macr: user,
          presetUser
        }
      })
      .afterClosed()
      .pipe(filter(Boolean));
  }

  protected handleError<T>(): OperatorFunction<T, T> {
    return catchError(error => {
      this.snackBarService.error(error.error ?? error);

      return EMPTY;
    });
  }
}
